<template>
  <div class="py-5">
    <b-container>
      <b-overlay :show="isLoading" bg-color="transparent" blur="2px">
        <template #overlay>
          <lottie-loading></lottie-loading>
        </template>
        <!-- loading text -->
        <b-card class="rounded-xl" v-if="!loadingLine">
          <h1 class="my-5 py-5">
            {{ isLoading ? "Sedang mengambil data ..." : "Belum ada data." }}
          </h1>
        </b-card>
        <!-- success text -->
        <b-card class="rounded-xl" v-else-if="isCreateSuccess">
          <b-card class="text-center">
            <lottie-loading
              src="static/assets/lottie/send-success.json"
              :loop="false"
              :speed="1"
              :height="150"
            />
            <h1>Request addendum sudah berhasil dikirim!</h1>
            <p>
              Silakan <b>tutup halaman ini</b> dan hubungi pihak persemaian
              terkait untuk kelanjutan proses distribusi addendum.
            </p>
          </b-card>
        </b-card>
        <!-- addendum -->
        <div v-else>
          <!-- detail & statistic -->
          <b-card class="rounded-xl animated mb-5">
            <b-card-title
              class="d-flex align-items-start justify-content-between"
            >
              <div>
                <lottie-loading
                  src="static/assets/lottie/request-bubble.json"
                  class="d-inline-block"
                  :loop="true"
                  :speed="1"
                  :height="50"
                />
                <h3 class="mb-0 d-inline-block">Request Addendum Distribusi</h3>
              </div>
              <img :src="$_config.logo.lg" alt="" class="max-h-75px mr-3" />
            </b-card-title>
            <!-- detail distributions & statistics -->
            <b-row>
              <!-- tableDetailItems -->
              <b-col cols="12" lg="6">
                <table-detail :items="tableDetailItems"></table-detail>
              </b-col>
              <!-- statistics -->
              <b-col cols="12" lg="6">
                <b-row>
                  <!-- Statistics -->
                  <b-col cols="auto">
                    <statistic-card
                      :data="{
                        title: 'Request',
                        icon: 'ri-message-2-line text-info',
                        value: totalSeedRequest,
                        second: {
                          title: 'Dikirim',
                          icon: 'ri-truck-line text-primary',
                          value: totalSeedLoaded,
                        },
                        third: {
                          title: 'Hilang',
                          icon: 'ri-close-circle-line text-danger',
                          value: totalSeedMissing,
                        },
                        fourth: {
                          title: 'Belum Dikirim',
                          icon: 'ri-chat-delete-line text-warning',
                          value: totalSeedNotLoad,
                        },
                      }"
                    ></statistic-card>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-card>
          <!-- alert perbedaan jenis -->
          <base-alert type="warning">
            Perhatian: Jika jumlah dan jenis bibit yang diminta <b>berbeda</b>,
            harap periksa <b>kalender distribusi</b> di Geko sebelum melakukan
            permintaan addendum distribusi.
          </base-alert>
          <!-- list per-jenis -->
          <b-card class="rounded-xl animated mb-5">
            <h5>
              <i class="ri-plant-line"></i>
              Daftar Per-Jenis
            </h5>
            <!-- per-jenis -->
            <apexchart
              :id="`chart-jenis`"
              :ref="`chart-jenis`"
              height="235px"
              :options.sync="chartOptions.perJenis"
              :series="chartPerJenis"
            ></apexchart>
            <!-- tabel per-jenis -->
            <b-btn
              block
              variant="light-info"
              size="sm"
              v-b-toggle="'tablePenlubSeedsCollapse'"
            >
              <i class="ri-table-line"></i>
              Lihat Tabel Per-Jenis
            </b-btn>
            <b-collapse id="tablePenlubSeedsCollapse">
              <b-table :fields="penlubSeedsFields" :items="penlubSeeds">
                <!-- tree_name -->
                <template #cell(tree_name)="{ item }">
                  <i
                    :class="{
                      'ri-error-warning-line text-danger':
                        item.request != item.total_load,
                      'ri-checkbox-circle-line text-primary':
                        item.request == item.total_load,
                    }"
                  ></i>
                  <span
                    :class="{
                      'font-weight-bold': true,
                      'text-danger': item.request != item.total_load,
                      'text-primary': item.request == item.total_load,
                    }"
                  >
                    {{ item.tree_name }}
                  </span>
                </template>
                <!-- request -->
                <template #cell(request)="{ item }">
                  <span>{{ item.request | parse("number") }}</span>
                </template>
                <!-- total_load -->
                <template #cell(total_load)="{ item }">
                  <span
                    :class="{
                      'font-weight-bold': true,
                      'text-danger': item.request != item.total_load,
                      'text-primary': item.request == item.total_load,
                    }"
                    >{{ item.total_load | parse("number") }}</span
                  >
                </template>
                <!-- total_received -->
                <template #cell(total_received)="{ item }">
                  <span>{{ item.total_received | parse("number") }}</span>
                </template>
                <!-- total_damaged -->
                <template #cell(total_damaged)="{ item }">
                  <span>{{ item.total_damaged | parse("number") }}</span>
                </template>
                <!-- total_missing -->
                <template #cell(total_missing)="{ item }">
                  <span
                    :class="{
                      'text-danger': item.total_missing > 0,
                    }"
                    >{{ item.total_missing | parse("number") }}</span
                  >
                </template>
                <!-- total_unload -->
                <template #cell(total_unload)="{ item }">
                  <span
                    v-if="item.request - item.total_load > -1"
                    :class="{
                      'font-weight-bold': true,
                      'text-danger': item.request - item.total_load > 0,
                    }"
                  >
                    {{ (item.request - item.total_load) | parse("number") }}
                  </span>
                  <span v-else>0</span>
                </template>
                <!-- total_resend -->
                <template #cell(total_resend)="{ item }">
                  <span
                    :class="{
                      'text-info font-weight-bold': item.total_resend > 0,
                    }"
                  >
                    {{ item.total_resend | parse("number") }}
                  </span>
                </template>
                <!-- total_return -->
                <template #cell(total_return)="{ item }">
                  <span
                    :class="{
                      'text-info font-weight-bold': item.total_return > 0,
                    }"
                  >
                    {{ item.total_return | parse("number") }}
                  </span>
                </template>
              </b-table>
            </b-collapse>
          </b-card>
          <!-- form -->
          <b-card class="rounded-xl animated">
            <h5 class="mb-5">
              <i class="ri-chat-smile-ai-line"></i>
              Permintaan Pengiriman Ulang
            </h5>
            <!-- statistics return -->
            <b-row class="mb-4">
              <b-col cols="auto">
                <statistic-card
                  :data="{
                    title: 'Belum Dikirim',
                    icon: 'ri-chat-delete-line text-warning',
                    value: totalSeedNotLoad,
                    second: {
                      title: 'Hilang',
                      icon: 'ri-close-circle-line text-danger',
                      value: totalSeedMissing,
                    },
                    third: {
                      title: 'Pengiriman Ulang',
                      icon: 'ri-message-2-line text-info',
                      value: totalSeedResend,
                    },
                    fourth: {
                      title: 'Kelebihan',
                      icon: 'ri-skip-up-line text-warning',
                      value: totalSeedOverload,
                    },
                    fifth: {
                      title: 'Pengembalian',
                      icon: 'ri-arrow-go-back-line text-info',
                      value: totalSeedReturn,
                    },
                  }"
                ></statistic-card>
              </b-col>
            </b-row>
            <ValidationObserver
              v-slot="{ handleSubmit }"
              v-if="listFarmerProblem.length"
            >
              <form
                @submit.prevent="handleSubmit(onSubmit)"
                autocomplete="off"
                style="position: relative"
              >
                <div
                  v-for="(farmer, fIndex) in listFarmerProblem"
                  :key="`list-farmer-problem-${fIndex}`"
                  class="mb-3 border-2 border-bottom pb-3"
                >
                  <b-row>
                    <b-col cols="auto">{{ fIndex + 1 }}.</b-col>
                    <b-col>
                      <!-- farmer detail -->
                      <p class="mb-1 font-weight-bold">
                        {{ farmer.farmer_name }}
                      </p>
                      <b-badge>
                        {{ farmer.farmer_no }}
                      </b-badge>
                      <!-- farmer seeds -->
                      <b-table
                        small
                        :key="`table.farmer.detail_seed_farmers${farmer.farmer_no}${fIndex}`"
                        :fields="farmerSeedFields"
                        :items="
                          farmer.detail_seed_farmers.filter(
                            (v) => v.return_description || v.total_resend
                          )
                        "
                      >
                        <!-- lahan_no -->
                        <template #cell(lahan_no)="{ item }">
                          <span class="font-weight-bold">
                            {{ item.lahan_no }}
                            <i
                              @click="copyToClipboard(item.lahan_no)"
                              class="ri-file-copy-line pointer"
                            ></i>
                          </span>
                        </template>
                        <!-- tree_name -->
                        <template #cell(tree_name)="{ item }">
                          <span class="font-weight-bold">
                            {{ item.tree_name }}
                          </span>
                        </template>
                        <!-- total_missing -->
                        <template #cell(total_missing)="{ item }">
                          <span
                            :class="{
                              'font-weight-bold': true,
                              'text-danger': item.total_missing > 0,
                            }"
                          >
                            {{ item.total_missing }}
                          </span>
                        </template>
                        <!-- total_not_load -->
                        <template #cell(total_not_load)="{ item }">
                          <span
                            :class="{
                              'font-weight-bold': true,
                              'text-warning': item.total_not_load > 0,
                            }"
                          >
                            {{ item.total_not_load }}
                          </span>
                        </template>
                        <!-- detail seed table -->
                        <template #row-details="{ item }">
                          <b-row class="justify-content-end">
                            <!-- row.item.total_resend -->
                            <b-col cols="12" lg="6">
                              <s-input
                                class="mb-2"
                                v-model="item.total_resend"
                                :item="{
                                  label: 'Pengiriman Ulang',
                                  icon: 'ri-message-2-line',
                                  type: 'number',
                                  validation: [
                                    `min_value:${item.resend_min_value}`,
                                    `max_value:${item.resend_max_value}`,
                                  ],
                                  placeholder: '',
                                }"
                              />
                              <s-input
                                class="mb-2"
                                v-model="item.resend_description"
                                :item="{
                                  label: 'Catatan Pengiriman Ulang',
                                  icon: 'ri-sticky-note-line',
                                  type: 'text',
                                  validation: [],
                                }"
                              />
                            </b-col>
                            <!-- item.total_return -->
                            <b-col
                              cols="12"
                              lg="6"
                              v-if="item.total_penlub < item.total_load"
                            >
                              <s-input
                                class="mb-2"
                                v-model="item.total_return"
                                :item="{
                                  label: 'Pengembalian',
                                  icon: 'ri-arrow-go-back-line',
                                  type: 'number',
                                  validation: [
                                    `min_value:${0}`,
                                    `max_value:${item.total_load || 0}`,
                                  ],
                                  placeholder: '',
                                }"
                              />
                              <s-input
                                class="mb-2"
                                v-model="item.return_description"
                                :item="{
                                  label: 'Catatan Pengembalian',
                                  icon: 'ri-sticky-note-line',
                                  type: 'text',
                                  validation: [],
                                }"
                              />
                            </b-col>
                          </b-row>
                        </template>
                      </b-table>
                    </b-col>
                  </b-row>
                </div>

                <!-- Pengembalian Bibit -->
                <div v-if="totalSeedReturn" class="animated mb-5">
                  <!-- return_date -->
                  <s-input
                    v-model="mainData.return_date"
                    :item="{
                      label: 'Tanggal Pengembalian Bibit',
                      type: 'date',
                      validation: ['required'],
                      placeholder: '',
                    }"
                  />
                </div>
                <!-- description -->
                <s-input
                  v-model="mainData.description"
                  :item="{
                    label: 'Catatan',
                    type: 'textarea',
                    validation: ['required'],
                    placeholder: '',
                  }"
                />

                <!-- statistics return -->
                <b-row class="mb-4">
                  <b-col cols="auto">
                    <statistic-card
                      :data="{
                        title: 'Belum Dikirim',
                        icon: 'ri-chat-delete-line text-warning',
                        value: totalSeedNotLoad,
                        second: {
                          title: 'Hilang',
                          icon: 'ri-close-circle-line text-danger',
                          value: totalSeedMissing,
                        },
                        third: {
                          title: 'Pengiriman Ulang',
                          icon: 'ri-message-2-line text-info',
                          value: totalSeedResend,
                        },
                        fourth: {
                          title: 'Kelebihan',
                          icon: 'ri-skip-up-line text-warning',
                          value: totalSeedOverload,
                        },
                        fifth: {
                          title: 'Pengembalian',
                          icon: 'ri-arrow-go-back-line text-info',
                          value: totalSeedReturn,
                        },
                      }"
                    ></statistic-card>
                  </b-col>
                </b-row>

                <!-- message alert lahan tidak ada pada penlub -->
                <base-alert type="danger" v-if="farmerLahanMissing.length">
                  Peringatan: Ada
                  <b>{{ farmerLahanMissing.length }} Lahan</b> yang tidak ada
                  pada data penilikan lubang.
                </base-alert>
                <b-button
                  block
                  type="submit"
                  variant="primary"
                  :disabled="
                    (totalSeedResend == 0 && totalSeedReturn == 0) ||
                    farmerLahanMissing.length
                      ? true
                      : false
                  "
                >
                  <i class="ri-save-3-line"></i>
                  Kirim Permintaan
                </b-button>
              </form>
            </ValidationObserver>
            <base-alert v-else type="warning">
              Tidak ada data bibit yang bermasalah. Silakan melakukan pengecekan
              ulang penerimaan status BAST.
            </base-alert>
          </b-card>
        </div>
      </b-overlay>
    </b-container>
  </div>
</template>

<script>
import chartOptions from "./request-distribution-addendum-chart-options";

export default {
  name: "FormNurseryManagementRequestDistributionAddendum",

  data: () => ({
    chartOptions: chartOptions,
    farmerSeedFields: [
      // lahan_no
      {
        key: "lahan_no",
        label: "Lahan",
      },
      // tree_name
      {
        key: "tree_name",
        label: "Jenis",
      },
      // total_penlub
      {
        key: "total_penlub",
        label: "Penlub",
      },
      // total_load
      {
        key: "total_load",
        label: "Dikirim",
      },
      // total_missing
      {
        key: "total_missing",
        label: "Hilang",
      },
      // total_not_load
      {
        key: "total_not_load",
        label: "Belum Dikirim",
      },
    ],
    isCreateSuccess: 0,
    isLoading: false,
    listPenlub: [],
    loadingLine: null,
    penlubSeedsFields: [
      // tree_name
      {
        key: "tree_name",
        label: "Jenis",
      },
      // request
      {
        key: "request",
        label: "Penlub",
      },
      // total_load
      {
        key: "total_load",
        label: "Dikirim",
      },
      // total_received
      // {
      //   key: "total_received",
      //   label: "Diterima",
      // },
      // total_damaged
      // {
      //   key: "total_damaged",
      //   label: "Rusak",
      // },
      // total_missing
      {
        key: "total_missing",
        label: "Hilang",
      },
      // total_unload
      {
        key: "total_unload",
        label: "Belum Dikirim",
      },
      // total_resend
      {
        key: "total_resend",
        label: "Pengiriman Ulang",
      },
      // total_return
      {
        key: "total_return",
        label: "Pengembalian Bibit",
      },
    ],
    mainData: {
      is_return: 0,
      is_resend: 0,
    },
    tableDetailItems: [],
    tableDetailSeedFarmers: [
      {
        label: "Lahan No",
        key: "lahan_no",
        sortable: true,
      },
      {
        label: "Jenis Bibit",
        key: "rel_tree_id",
        sortable: true,
      },
      {
        label: "Permintaan",
        key: "total_penlub",
      },
      {
        label: "Dimuat",
        key: "total_load",
      },
      {
        label: "Rusak",
        key: "total_damaged",
      },
      {
        label: "Diterima",
        key: "total_received",
      },
      {
        label: "Hilang",
        key: "total_missing",
      },
      { label: "", key: "actions" },
    ],
    tableSummariesFields: [
      {
        label: "Jenis Bibit",
        key: "rel_tree_id",
        sortable: true,
      },
      {
        label: "Permintaan",
        key: "total_penlub",
        sortable: true,
      },
      {
        label: "Dimuat",
        key: "total_load",
        sortable: true,
      },
      {
        label: "Rusak",
        key: "total_damaged",
        sortable: true,
      },
      {
        label: "Diterima",
        key: "total_received",
        sortable: true,
      },
      {
        label: "Hilang",
        key: "total_missing",
        sortable: true,
      },
      {
        label: "Pengembalian",
        key: "total_return",
        sortable: true,
      },
      {
        label: "Request Pengiriman Ulang",
        key: "total_resend",
        sortable: true,
      },
    ],
    tableSummariesItems: [],
  }),

  computed: {
    penlubSeeds() {
      if (!this.listPenlub.length) return [];

      return (
        this.listPenlub
          .reduce((acc, lahan) => {
            lahan.detail_planting_hole.forEach((jenis) => {
              let distributionSeeds = {
                total_load: 0,
                total_received: 0,
                total_damaged: 0,
                total_missing: 0,
                total_resend: 0,
                total_return: 0,
              };

              if (this.loadingLine) {
                const findFarmer = this.loadingLine.detail_farmers.find(
                  (farmer) => farmer.farmer_no == lahan.farmer_no
                );
                if (findFarmer) {
                  const findFarmerSeed = findFarmer.detail_seed_farmers.find(
                    (farmerSeed) =>
                      farmerSeed.tree_code == jenis.tree_code &&
                      farmerSeed.lahan_no == lahan.lahan_no
                  );
                  if (findFarmerSeed) {
                    // total_load
                    distributionSeeds.total_load = parseInt(
                      findFarmerSeed.total_load
                    );
                    // total_received
                    distributionSeeds.total_received = parseInt(
                      findFarmerSeed.total_received
                    );
                    // total_damaged
                    distributionSeeds.total_damaged = parseInt(
                      findFarmerSeed.total_damaged
                    );
                    // total_missing
                    distributionSeeds.total_missing = parseInt(
                      findFarmerSeed.total_missing
                    );
                    // total_resend
                    distributionSeeds.total_resend = parseInt(
                      findFarmerSeed.total_resend
                    );
                    // total_return
                    distributionSeeds.total_return = parseInt(
                      findFarmerSeed.total_return
                    );
                  }
                }
              }

              const existingItem = acc.find(
                (item) => item.tree_code === jenis.tree_code
              );
              if (existingItem) {
                existingItem.request += parseInt(jenis.amount);
                existingItem.total_load += distributionSeeds.total_load || 0;
                existingItem.total_received +=
                  distributionSeeds.total_received || 0;
                existingItem.total_damaged +=
                  distributionSeeds.total_damaged || 0;
                existingItem.total_missing +=
                  distributionSeeds.total_missing || 0;
                existingItem.total_resend +=
                  distributionSeeds.total_resend || 0;
                existingItem.total_return +=
                  distributionSeeds.total_return || 0;
              } else {
                acc.push({
                  tree_code: jenis.tree_code,
                  tree_name: jenis.tree_name,
                  request: parseInt(jenis.amount),
                  ...distributionSeeds,
                });
              }
            });
            return acc;
          }, [])
          // add total_unload
          .map((v) => {
            return {
              ...v,
              total_not_load:
                v.request - v.total_load > 0 ? v.request - v.total_load : 0,
            };
          })
          // sorting_data
          .sort((a, b) => b.request - a.request)
      );
    },
    chartPerJenis() {
      return [
        // request
        {
          name: "Penlub",
          data: this.penlubSeeds.map((v) => {
            return {
              x: v.tree_name,
              y: v.request,
            };
          }),
        },
        // total_load
        {
          name: "Dikirim",
          data: this.penlubSeeds.map((v) => {
            return {
              x: v.tree_name,
              y: v.total_load,
            };
          }),
        },
        // total_received
        // {
        //   name: "Diterima",
        //   data: this.penlubSeeds
        //     .map((v) => {
        //       return {
        //         x: v.tree_name,
        //         y: v.total_received,
        //       };
        //     }),
        // },
        // total_damaged
        // {
        //   name: "Rusak",
        //   color: "#f00",
        //   data: this.penlubSeeds
        //     .map((v) => {
        //       return {
        //         x: v.tree_name,
        //         y: v.total_damaged,
        //       };
        //     }),
        // },
        // total_missing
        {
          name: "Hilang",
          data: this.penlubSeeds.map((v) => {
            return {
              x: v.tree_name,
              y: v.total_missing,
            };
          }),
        },
        // total_not_load
        {
          name: "Belum Dikirim",
          data: this.penlubSeeds.map((v) => {
            return {
              x: v.tree_name,
              y: v.total_not_load,
            };
          }),
        },
        // total_resend
        {
          name: "Pengiriman Ulang",
          data: this.penlubSeeds.map((v) => {
            return {
              x: v.tree_name,
              y: v.total_resend,
            };
          }),
        },
        // total_return
        {
          name: "Pengembalian Bibit",
          data: this.penlubSeeds.map((v) => {
            return {
              x: v.tree_name,
              y: v.total_return,
            };
          }),
        },
      ];
    },
    totalSeedRequest() {
      return this.penlubSeeds.reduce((acc, seed) => acc + seed.request, 0);
    },
    totalSeedLoaded() {
      return this.penlubSeeds.reduce(
        (acc, seed) => acc + (seed.total_load || 0),
        0
      );
    },
    totalSeedReceived() {
      return this.penlubSeeds.reduce(
        (acc, seed) => acc + (seed.total_received || 0),
        0
      );
    },
    totalSeedMissing() {
      return this.penlubSeeds.reduce(
        (acc, seed) => acc + (seed.total_missing || 0),
        0
      );
    },
    totalSeedDamaged() {
      return this.penlubSeeds.reduce(
        (acc, seed) => acc + (seed.total_damaged || 0),
        0
      );
    },
    totalSeedNotLoad() {
      return this.penlubSeeds.reduce(
        (acc, seed) => acc + (seed.total_not_load || 0),
        0
      );
    },
    totalSeedOverload() {
      return this.penlubSeeds.reduce((acc, seed) => {
        let over = 0;
        if (seed.total_load > seed.request) {
          over += parseInt(seed.total_load) - parseInt(seed.request);
        }
        return acc + over;
      }, 0);
    },
    listFarmerProblem() {
      if (!this.loadingLine) return [];

      return this.loadingLine.detail_farmers
        .filter((v) => v.is_resend || v.is_return)
        .sort((a, b) => a.farmer_name.localeCompare(b.farmer_name));
    },
    totalSeedResend() {
      return this.listFarmerProblem.reduce((acc, farmer) => {
        return (
          acc +
          farmer.detail_seed_farmers.reduce((acc2, seed) => {
            return acc2 + parseInt(seed.total_resend || 0);
          }, 0)
        );
      }, 0);
    },
    totalSeedReturn() {
      return this.listFarmerProblem.reduce((acc, farmer) => {
        return (
          acc +
          farmer.detail_seed_farmers.reduce((acc2, seed) => {
            return acc2 + parseInt(seed.total_return || 0);
          }, 0)
        );
      }, 0);
    },
    farmerLahanMissing() {
      if (!this.loadingLine) return [];

      return this.loadingLine.detail_farmers
        .filter((v) => v.is_resend || v.is_return)
        .filter((v) =>
          v.detail_seed_farmers.some(
            (s) => !this.listPenlub.some((p) => p.lahan_no == s.lahan_no)
          )
        )
        .sort((a, b) => a.farmer_name.localeCompare(b.farmer_name));
    },
  },

  mounted() {
    this.initForm();
  },

  methods: {
    afterCreateSucceed: async function () {
      this.$_alert
        .show({
          icon: "success",
          title: "Sukses!",
          text: "Berhasil mengirim data permintaan addendum distribusi!",
        })
        .then(() => {
          this.isCreateSuccess = 1;
          this.$router.push({ path: this.$route.path, query: {} });
          window.close();
        });
    },
    checkData: async function () {
      try {
        this.loadingLine.detail_farmers.map((farmer) => {
          let unload = 0;
          let tooMuch = 0;
          farmer.detail_seed_farmers.map((seed) => {
            const totalPenlub = parseInt(seed.total_penlub);
            const totalLoad = parseInt(seed.total_load);
            const totalDamaged = parseInt(seed.total_damaged);
            const totalMissing = parseInt(seed.total_missing);
            const totalReceived = parseInt(seed.total_received);
            const diff = totalPenlub - totalLoad;

            if (diff > 0) {
              unload += diff;
              seed.total_not_load = diff;
              seed.total_resend = diff + totalMissing;

              // seed.resend_min_value = diff;
              seed.resend_min_value = 0;

              seed.resend_max_value = totalPenlub - totalReceived;
              seed.resend_description = `Kurang ${
                totalMissing ? "& Hilang " : ""
              }`;
              seed.total_return = 0;
              seed.return_description = "";
            } else if (diff < 0) {
              tooMuch += diff;
              seed.total_not_load = 0;
              // seed.total_return = diff * -1;
              seed.total_return = 0;
              seed.return_min_value = 0;
              seed.return_description = `Kelebihan`;
              seed.total_resend = 0;
              seed.resend_description = "";
            }

            // add showDetails for detail table
            seed._showDetails = true;
          });
          if (unload > 0) {
            farmer.is_resend = 1;
            farmer.resend_toggle_disabled = true;
          }
          if (tooMuch * -1 > 0) {
            farmer.is_return = 1;
            farmer.return_toggle_disabled = true;
          }
        });
      } catch (err) {
        console.log("checkData() error", err);
        this.$_alert.error(err);
      }
    },
    copyToClipboard(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$_alert.success("Text copied to clipboard");
        })
        .catch((err) => {
          console.log("copyToClipboard() error", err);
          this.$_alert.error("Failed to copy");
        });
    },
    getPlantingHoleGeko: async function (trees) {
      try {
        let plantingHoles = await this.$_api
          .get(`public/list`, {
            model: "distribution",
            limit: 100,
            program_year: this.loadingLine.program_year,
            ff_no: this.loadingLine.ff_no,
          })
          .then((res) => res.data);
        this.listPenlub = plantingHoles;
        for (const [phIndex, phVal] of Object.entries(plantingHoles)) {
          const detailPH = phVal.detail_planting_hole;

          const findFarmer = this.loadingLine.detail_farmers.find(
            (f) => f.farmer_no == phVal.farmer_no
          );
          if (findFarmer) {
            detailPH.map((dps) => {
              const findSeedFarmer = findFarmer.detail_seed_farmers.find(
                (dsf) =>
                  dsf.tree_code == dps.tree_code &&
                  dsf.lahan_no == phVal.lahan_no
              );
              if (findSeedFarmer)
                findSeedFarmer.total_penlub += parseInt(dps.amount);
              // create  detail_seed_farmers if tree is not loaded
              else {
                const findMainTree = trees.find(
                  (tree) => tree.tree_code == dps.tree_code
                );
                if (findMainTree) {
                  findFarmer.detail_seed_farmers.push({
                    detail_farmer_id: findFarmer.id,
                    total_damaged: 0,
                    total_load: 0,
                    total_not_load: 0,
                    total_missing: 0,
                    total_received: 0,
                    total_resend: 0,
                    total_return: 0,
                    lahan_no: phVal.lahan_no,
                    tree_code: dps.tree_code,
                    tree_id: findMainTree.id,
                    rel_tree_id: findMainTree.tree_name,
                    tree_name: findMainTree.tree_name,
                    total_penlub: parseInt(dps.amount),
                    newItem: true,
                  });
                }
              }
            });
          }
        }

        return true;
      } catch (err) {
        console.log("getPlantingHoleGeko() error", err);
        this.$_alert.error(err);
      }
    },
    getTrees: async function () {
      try {
        let trees = await this.$_api
          .get("public/list", {
            model: "master_trees",
            limit: "500",
          })
          .then((res) => res.data);

        return trees;
      } catch (err) {
        console.log("initForm() error", err);
        this.$_alert.error(err);
      }
    },
    initForm: async function () {
      try {
        this.isLoading = true;
        this.loadingLine = null;
        const routeQ = this.$route.query;
        const loadingLineID = routeQ ? routeQ.loading_line_id : null;
        const requestBy = routeQ ? routeQ.request_by : null;
        if (!loadingLineID || !requestBy) {
          loading.close();
          setTimeout(() => {
            this.$_alert.error(null, "Gagal", "Gagal memuat data distribusi!");
          }, 100);
          return;
        }

        const trees = await this.getTrees();

        // get data distribution
        let loadingLine = await this.$_api
          .get("public/show", {
            model: "loading_line",
            id: loadingLineID,
          })
          .then((res) => {
            // prepare for data request
            return {
              ...res.data,
              detail_farmers: res.data.detail_farmers
                .map((f) => {
                  return {
                    ...f,
                    is_return: 0,
                    is_resend: 0,
                    detail_seed_farmers: f.detail_seed_farmers.map((dsf) => {
                      return {
                        ...dsf,
                        total_penlub: 0,
                        total_not_load: 0,
                        total_resend: 0,
                        resend_min_value: 0,
                        resend_description: "",
                        total_return: 0,
                        return_min_value: 0,
                        return_description: "",
                      };
                    }),
                  };
                })
                .sort((a, b) => a.farmer_name.localeCompare(b.farmer_name)),
            };
          });
        this.loadingLine = await loadingLine;

        const getPHGEKO = await this.getPlantingHoleGeko(trees);
        if (getPHGEKO) {
          await this.initTableDetailItems(loadingLine);

          await this.checkData();
        }
      } catch (err) {
        console.log("initForm() error", err);
        setTimeout(() => {
          this.$_alert.error(err);
        }, 100);
      } finally {
        this.isLoading = false;
      }
    },
    initTableDetailItems: async function (loadingLine) {
      const items = [
        {
          label: "Tahun Program",
          value: loadingLine.program_year,
          valueClass: "badge badge-info",
        },
        {
          label: "Lokasi Persemaian",
          value: loadingLine.rel_location_nursery_id,
          valueClass: "badge badge-primary",
        },
        {
          label: "Management Unit",
          value: loadingLine.mu_name,
        },
        {
          label: "Tanggal Distribusi",
          value: loadingLine.distribution_date,
          transform: "longDate",
          valueClass: "font-weight-bold",
        },
        {
          label: "Nama FF",
          value: loadingLine.ff_name,
          sub_value: loadingLine.ff_no,
        },
      ];
      this.tableDetailItems = items;
    },
    onSubmit: async function () {
      if (!this.totalSeedResend && !this.totalSeedReturn) {
        this.$_alert.error(
          null,
          "Gagal",
          "Tidak ada pengembalian atau request pengiriman ulang bibit!"
        );
        return;
      }
      const confirm = await this.$_alert.confirm(
        "Konfirmasi",
        "Apakah Anda yakin ingin mengirimkan permintaan distribusi addendum?"
      );
      if (!confirm.isConfirmed) return;

      const loading = this.$_alert.loading("Mengirim request ...");
      try {
        this.isLoading = true;
        // check existing request
        // const checkExisting = await this.$_api
        //   .get("public/list", {
        //     model: "detail_adendum",
        //     loading_line_id: this.loadingLine.id,
        //   })
        //   .then((res) => res.data);
        // if (checkExisting.length) {
        //   this.$_alert.error(
        //     null,
        //     "Sudah Ada",
        //     "Sudah pernah melakukan request addendum pada ff ini! Silakan ditunggu pengiriman dari persemaian atau bisa menghubungi pihak persemaian terkait."
        //   );
        //   return;
        // }

        const storeMain = {
          model: "detail_adendum",
          loading_line_id: this.loadingLine.id,
          request_by: this.$route.query.request_by,
          status_verified: 0,
          status_received: 0,
          verified_by: null,
          return_date: this.mainData.is_return
            ? this.mainData.date_pengembalian_bibit
            : null,
          distribution_date: null,
          description: this.mainData.description,
          status_allocation: 0,
          list_adendum: [],
        };
        // set list_adendum data
        this.loadingLine.detail_farmers.map((f) => {
          if (f.is_return || f.is_resend) {
            f.detail_seed_farmers.map((fSeed) => {
              const diffResend = parseInt(fSeed.resend_min_value || 0);
              const diffReturn = parseInt(fSeed.return_min_value || 0);
              const totalReturn = parseInt(fSeed.total_return || 0);
              const totalResend = parseInt(fSeed.total_resend || 0);
              const totalPenlub = parseInt(fSeed.total_penlub || 0);
              const totalLoad = parseInt(fSeed.total_load || 0);
              const totalReceived = parseInt(fSeed.total_received || 0);
              const dAdendum = {
                farmer_no: f.farmer_no,
                lahan_no: fSeed.lahan_no,
                farmer_name: f.farmer_name,
                // before_qty_seed == total request == total penlub
                before_qty_seed: totalPenlub,
                tree_id: fSeed.tree_id,
              };

              if (f.is_return && totalReturn > 0)
                storeMain.list_adendum.push({
                  ...dAdendum,
                  category_id: 1,
                  after_qty_seed: totalReturn,
                  description_reason: fSeed.return_description,
                });

              if (f.is_resend && totalResend > 0)
                storeMain.list_adendum.push({
                  ...dAdendum,
                  category_id: 2,
                  after_qty_seed: totalResend,
                  description_reason: fSeed.resend_description,
                });
            });
          }
        });
        console.log("storeMain", storeMain);
        await this.$_api.post("public/create", storeMain);
        await loading.close();
        setTimeout(() => {
          this.afterCreateSucceed();
        }, 100);
      } catch (err) {
        console.log(`onSubmit() error`, err);
        await loading.close();
        setTimeout(() => {
          this.$_alert.error(err);
        }, 100);
      } finally {
        this.isLoading = false;
        await loading.close();
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/default-page/forms/request-distribution-addendum.vue